import type { CategoryContent } from '../row.js';

/** Physics, Chemistry and Biology, second chunk. Correct option first; rotated at build. */

export const MORE_PHYSICS: CategoryContent = {
  msc: [
    [
      'What does the Debye model improve on relative to Einstein\'s model of heat capacity?',
      [
        'It treats lattice vibrations as a spectrum of modes',
        'It includes the electronic contribution to heat capacity at low temperature',
        'It accounts for anharmonic corrections at high temperature',
        'It treats each atom as an independent oscillator at a single frequency',
      ],
      0,
      'The single-frequency treatment is Einstein\'s, and it falls off exponentially where experiment shows a power law.',
    ],
    [
      'The Schwarzschild radius of a mass M is proportional to what?',
      ['M', 'M squared', 'The square root of M', 'The logarithm of M'],
      0,
      '2GM/c-squared, so it scales linearly - which is why a supermassive black hole has such low tidal forces at its horizon.',
    ],
    [
      'What does the fluctuation-dissipation theorem relate?',
      [
        'Spontaneous fluctuations in a system at equilibrium to its linear response to perturbation',
        'The entropy of a system to the logarithm of its accessible microstates',
        'Heat flow to the temperature gradient in a conducting medium',
        'The pressure of a gas to the mean square velocity of its molecules',
      ],
      0,
      'Johnson noise in a resistor being the textbook case: the noise and the resistance are the same physics viewed twice.',
    ],
    [
      'In special relativity, what quantity is invariant between inertial frames?',
      [
        'The spacetime interval',
        'The time between two events',
        'The spatial distance between two events',
        'The total momentum of an isolated system',
      ],
      0,
      'Momentum is conserved within a frame but not invariant between them. The interval is what replaces separate space and time invariance.',
    ],
    [
      'What is the significance of the Fermi energy in a metal?',
      [
        'The highest occupied electron energy level at zero temperature, setting most electronic properties',
        'The energy required to remove an electron entirely from the metal',
        'The mean thermal energy of conduction electrons at room temperature',
        'The energy gap between valence and conduction bands, which determines whether a solid conducts at all',
      ],
      0,
      'Removing the electron altogether is the work function. Only electrons within about kT of the Fermi level participate in conduction, which is why classical predictions for heat capacity failed so badly.',
    ],
    [
      'The Stefan-Boltzmann law states that radiated power scales as which power of temperature?',
      ['The fourth', 'The second', 'The third', 'The first'],
      0,
      'Which is why a modest temperature rise makes such a large difference to radiative cooling, and why climate feedback analysis starts there.',
    ],
    [
      'What does a phase diagram\'s triple point specify?',
      [
        'The unique conditions at which solid, liquid and vapour coexist in equilibrium',
        'The conditions above which liquid and vapour become indistinguishable',
        'The pressure at which a solid sublimes directly to vapour',
        'The temperature at which a substance\'s heat capacity diverges',
      ],
      0,
      'The liquid-vapour indistinguishability point is the critical point. Water\'s triple point defined the kelvin until the 2019 redefinition.',
    ],
    [
      'The Hubble parameter has dimensions of what?',
      [
        'Inverse time',
        'Velocity',
        'Acceleration',
        'Inverse length',
      ],
      0,
      'Which is why its reciprocal is a rough age of the universe, and why km/s/Mpc is a unit that hides its own dimensions.',
    ],
    [
      'What is the physical content of Noether\'s theorem?',
      [
        'Every continuous symmetry of the action corresponds to a conserved quantity',
        'Every conserved quantity implies an underlying discrete symmetry',
        'Symmetries of a Hamiltonian commute with the time evolution operator',
        'Any Lagrangian can be written in a form invariant under coordinate change',
      ],
      0,
      'Time translation gives energy, space translation momentum, rotation angular momentum. It is the reason conservation laws are not coincidences.',
    ],
    [
      'What does the term "degeneracy pressure" refer to?',
      [
        'Pressure arising from the Pauli exclusion principle',
        'Radiation pressure from photons trapped in a stellar interior',
        'Pressure from relativistic electrons approaching the speed of light',
        'Pressure exerted by neutrinos escaping a collapsing core',
      ],
      0,
      'It is what supports white dwarfs and neutron stars, and its temperature independence is exactly why they cannot regulate themselves the way a main-sequence star does.',
    ],
  ],
  phd: [
    [
      'What does spontaneous symmetry breaking supply in the Standard Model?',
      [
        'Masses for the weak gauge bosons and fermions without violating gauge invariance',
        'The running of the coupling constants with energy scale',
        'Confinement of colour charge at low energies',
        'The cancellation of chiral anomalies between generations',
      ],
      0,
      'A mass term inserted by hand would break gauge invariance; the Higgs mechanism gets the same result while preserving it.',
    ],
    [
      'What is the physical meaning of the renormalisation group?',
      [
        'How effective couplings change with the scale at which a theory is probed',
        'A procedure for removing infinities by discarding divergent integrals',
        'The requirement that all observables be invariant under gauge transformations',
        'A method for summing perturbation series to all orders',
      ],
      0,
      'Wilson\'s reframing turned renormalisation from a subtraction trick into a statement about which degrees of freedom matter at which scale.',
    ],
    [
      'The Bell inequalities, when violated experimentally, rule out what?',
      [
        'Local hidden-variable theories reproducing quantum predictions',
        'Any interpretation of quantum mechanics involving hidden variables',
        'The possibility of superluminal signalling',
        'The completeness of the quantum state description',
      ],
      0,
      'Non-local hidden variables - Bohmian mechanics - survive. The distinction between local and any hidden variables is where most popular accounts go wrong.',
    ],
    [
      'What does the Landau theory of phase transitions do?',
      [
        'Expands free energy in powers of an order parameter to classify transitions by symmetry',
        'Computes critical exponents exactly in three dimensions',
        'Derives transition temperatures from microscopic interaction potentials',
        'Treats fluctuations exactly near the critical point',
      ],
      0,
      'It is mean-field and therefore gets the exponents wrong near criticality - which is what the renormalisation group was needed to fix.',
    ],
    [
      'What is the Eddington limit?',
      [
        'The luminosity at which radiation pressure balances gravity, capping steady accretion',
        'The maximum mass a star can have before core collapse, set by electron degeneracy pressure',
        'The maximum rotation rate before a star becomes unstable',
        'The limiting temperature of an accretion disc\'s inner edge',
      ],
      0,
      'It is why supermassive black hole growth has a natural timescale, and why super-Eddington accretion is such an interesting problem.',
    ],
    [
      'What does the WKB approximation assume?',
      [
        'The potential varies slowly compared to the local de Broglie wavelength',
        'The perturbation is small compared to the unperturbed Hamiltonian',
        'The system is close to its ground state throughout',
        'The wavefunction can be expanded in a complete set of plane waves',
      ],
      0,
      'It fails exactly at turning points, which is why matched asymptotics and the Airy function turn up in every treatment of it.',
    ],
    [
      'What is the significance of the cosmic microwave background\'s acoustic peaks?',
      [
        'Their positions and heights constrain curvature, baryon density and dark matter density together',
        'They record the epoch of reionisation by the first stars',
        'They arise from gravitational lensing by intervening structure',
        'They measure the peculiar velocity of the Local Group, which appears as a dipole in the temperature map',
      ],
      0,
      'The first peak\'s angular position is the geometric ruler; the odd-even peak ratio is what pins the baryons.',
    ],
    [
      'In condensed matter, what characterises a topological insulator?',
      [
        'An insulating bulk with conducting surface states protected by topology and time-reversal symmetry',
        'A conducting bulk with insulating boundaries at low temperature',
        'A material whose resistivity is exactly quantised in the bulk, independent of sample geometry or disorder',
        'A superconductor whose gap closes at isolated points in momentum space',
      ],
      0,
      'The protection is what makes them interesting: the surface conduction survives disorder that would localise an ordinary surface state.',
    ],
    [
      'What does the Sunyaev-Zel\'dovich effect measure?',
      [
        'Distortion of the CMB spectrum by inverse Compton scattering off hot cluster electrons',
        'The redshift of cluster galaxies relative to the CMB dipole',
        'Gravitational redshift of photons escaping a cluster potential',
        'Absorption of CMB photons by intervening neutral hydrogen',
      ],
      0,
      'Its redshift independence is the useful part: clusters can be found by it at distances where they are too faint to see optically.',
    ],
    [
      'What problem does inflation solve, and what does it not?',
      [
        'It addresses horizon and flatness problems but does not by itself explain the initial conditions it assumes',
        'It explains the baryon asymmetry but not the flatness of space',
        'It explains dark energy but not the origin of structure',
        'It resolves the singularity at the big bang entirely, replacing it with an eternally inflating prior phase of expansion',
      ],
      0,
      'The measure problem and the initial-conditions question are why the "inflation explains everything" reading gets pushback from within cosmology.',
    ],
  ],
  professor: [
    [
      'What is the black hole information paradox, precisely stated?',
      [
        'Hawking radiation appears thermal',
        'The singularity destroys information before it can be radiated away',
        'No observer can measure the interior state, so information is undefined',
        'Entropy of the horizon exceeds the entropy of the collapsing matter',
      ],
      0,
      'The Page curve is the sharpened version, and the recent island calculations are what made progress possible.',
    ],
    [
      'The AdS/CFT correspondence conjectures what?',
      [
        'A gravitational theory in anti-de Sitter space is dual to a conformal field theory on its boundary',
        'Any quantum field theory has a gravitational dual in one higher dimension',
        'String theory in flat space is equivalent to a gauge theory at strong coupling',
        'Black hole entropy equals the entanglement entropy of the vacuum',
      ],
      0,
      'Maldacena, 1997. The generalisation to any field theory is exactly what is not established, and our universe is not anti-de Sitter.',
    ],
    [
      'What does the Kibble-Zurek scaling predict for a slow quench?',
      [
        'Defect density falls as a power of the quench rate',
        'Defect density is independent of quench rate below a threshold',
        'Defects anneal completely if the quench is slower than the relaxation time',
        'Defect density scales exponentially with the correlation length',
      ],
      0,
      'It has now been tested in cold atoms, ion chains and superfluids, which is more than the cosmological application it was invented for can offer.',
    ],
    [
      'What is the strong CP problem?',
      [
        'QCD permits a CP-violating term whose coefficient is measured to be extraordinarily small with no explanation',
        'CP violation in the weak sector is too small to account for baryogenesis',
        'The strong coupling constant diverges at low energies, so perturbation theory fails in the confining regime entirely',
        'Quark masses cannot be made real by any chiral rotation',
      ],
      0,
      'The theta term. The Peccei-Quinn solution is where the axion comes from, which is why it is simultaneously a dark matter candidate.',
    ],
    [
      'What does the Hawking-Page transition describe?',
      [
        'A first-order transition between thermal AdS and a large black hole as temperature rises',
        'The evaporation endpoint at which a black hole becomes a remnant',
        'The transition from a radiation-dominated to a matter-dominated universe',
        'The onset of superradiance in a rotating black hole',
      ],
      0,
      'In the holographic dictionary it maps onto confinement-deconfinement in the boundary theory, which is a large part of why it matters.',
    ],
    [
      'What is the quantum Hall effect\'s conductance quantisation explained by?',
      [
        'A topological invariant, the Chern number, of the filled Landau bands',
        'The exact cancellation of impurity scattering at low temperature',
        'The formation of a Wigner crystal in the two-dimensional electron gas',
        'Flux quantisation in the superconducting contacts of the device',
      ],
      0,
      'TKNN, 1982. Its precision is why it now underpins the resistance standard - a topological argument in a metrology laboratory.',
    ],
    [
      'What is the cosmological constant problem?',
      [
        'Field-theoretic estimates of vacuum energy exceed the observed value by many tens of orders of magnitude',
        'The observed expansion rate is inconsistent with a constant vacuum energy',
        'Dark energy cannot be distinguished observationally from modified gravity',
        'The constant is not invariant under general coordinate transformations, so its value depends on the chosen frame',
      ],
      0,
      'The worst quantitative prediction in physics, by some accounting. Anthropic and landscape arguments are what remain in the absence of a mechanism.',
    ],
    [
      'What does the Sachdev-Ye-Kitaev model provide?',
      [
        'A solvable strongly-coupled model with maximal chaos, connected to holography and black hole dynamics',
        'An exact solution of the three-dimensional Ising model, from which the critical exponents follow directly',
        'A tractable lattice model of high-temperature superconductivity',
        'A derivation of Fermi liquid theory from first principles',
      ],
      0,
      'Saturating the chaos bound is what links it to gravity, and being solvable is what makes it useful.',
    ],
    [
      'What is the current status of the Hubble tension?',
      [
        'Local distance-ladder and CMB-inferred expansion rates disagree beyond quoted uncertainties, with no accepted resolution',
        'The tension was resolved by revised Cepheid calibration',
        'It arises entirely from an unmodelled local void, so the local measurement is correct and simply unrepresentative of the global rate',
        'It disappears when supernova samples are restricted to low redshift',
      ],
      0,
      'Whether it is systematics or new physics is precisely what is unsettled, and JWST calibration has narrowed the systematics case without closing it.',
    ],
    [
      'What does "eigenstate thermalisation" propose?',
      [
        'Individual highly-excited eigenstates of a many-body system already encode thermal expectation values',
        'Thermalisation requires coupling to an external bath in all cases, so an isolated system can never thermalise',
        'Isolated quantum systems cannot thermalise because evolution is unitary',
        'Thermal behaviour emerges only after averaging over initial states',
      ],
      0,
      'It reconciles unitary microscopic evolution with thermal macroscopic behaviour, and many-body localisation is the interesting exception to it.',
    ],
  ],
};

export const MORE_CHEMISTRY: CategoryContent = {
  msc: [
    [
      'What does Le Chatelier\'s principle predict for an equilibrium under increased pressure?',
      [
        'A shift toward the side with fewer moles of gas',
        'A shift toward the side with more moles of gas',
        'An increase in the equilibrium constant',
        'No change, since pressure does not affect equilibria',
      ],
      0,
      'The constant itself does not change with pressure - only the position does, which is the distinction the principle is usually stated too loosely to capture.',
    ],
    [
      'A Grignard reagent is what, and what does it not tolerate?',
      [
        'An organomagnesium halide, incompatible with protic solvents and acidic hydrogens',
        'An organolithium species, incompatible only with water',
        'An organozinc species, tolerant of alcohols and amines',
        'An organocopper species, incompatible with ethereal solvents and unreactive toward ketones',
      ],
      0,
      'Its basicity is the limitation: any acidic proton quenches it, which is why the substrate scope is so much narrower than the reaction looks.',
    ],
    [
      'What does the Beer-Lambert law relate?',
      [
        'Absorbance to concentration, path length and molar absorptivity',
        'Fluorescence intensity to excitation wavelength',
        'Refractive index to solute concentration',
        'Scattering intensity to particle radius',
      ],
      0,
      'It fails at high concentration where the assumption of non-interacting absorbers breaks down, which is why calibration curves flatten.',
    ],
    [
      'What is the entropy change when an ideal gas expands isothermally into a vacuum?',
      [
        'Positive, since the accessible volume increases',
        'Zero, since the temperature is unchanged',
        'Negative, since no work is done on the surroundings',
        'Zero, since the process is adiabatic',
      ],
      0,
      'nR ln(V2/V1). Free expansion is irreversible, which is exactly why entropy rises with no heat transfer.',
    ],
    [
      'What is a chiral centre?',
      [
        'An atom bearing substituents arranged so the molecule is not superimposable on its mirror image',
        'Any carbon atom bonded to four different atoms of the same element',
        'A double bond preventing rotation and giving distinct isomers',
        'A ring junction at which two rings share two atoms, which fixes the relative geometry of both',
      ],
      0,
      'The double-bond case is geometric isomerism, which produces distinct compounds but not enantiomers.',
    ],
    [
      'What does a Zeta potential measurement tell you about a colloid?',
      [
        'The electrostatic potential at the slipping plane, indicating suspension stability',
        'The mean hydrodynamic radius of the dispersed particles',
        'The surface tension between the dispersed and continuous phases',
        'The viscosity of the continuous phase at the particle surface',
      ],
      0,
      'Magnitudes above roughly 30 millivolts either way generally mean the suspension will resist aggregation.',
    ],
    [
      'What is the difference between a syn and an anti addition?',
      [
        'Whether both new bonds form on the same face of the substrate or on opposite faces',
        'Whether the reaction proceeds through a radical or an ionic mechanism',
        'Whether the reaction is under kinetic or thermodynamic control',
        'Whether the product is racemic or enantioenriched',
      ],
      0,
      'Osmium tetroxide dihydroxylation is syn; bromine addition through a bromonium ion is anti - and the stereochemistry is how you diagnose the mechanism.',
    ],
    [
      'What is a common-ion effect?',
      [
        'Reduced solubility of a salt when a solution already contains one of its ions',
        'Increased solubility of a salt in the presence of a complexing agent',
        'The tendency of ions of similar radius to substitute in a lattice',
        'The equal migration rates of ions of the same charge in a field',
      ],
      0,
      'A direct application of Le Chatelier to a solubility product, and the basis of most gravimetric precipitation procedures.',
    ],
    [
      'What does a mass spectrum\'s M+2 peak of roughly equal intensity to M+ suggest?',
      [
        'The presence of a bromine atom',
        'The presence of a chlorine atom',
        'The presence of two nitrogen atoms',
        'A doubly charged molecular ion',
      ],
      0,
      'Bromine\'s isotopes are near 1:1; chlorine gives roughly 3:1, which is the pair you learn to read off by eye.',
    ],
    [
      'What is the purpose of a Buchner funnel with a water aspirator?',
      [
        'Filtration under reduced pressure, to separate a solid from a solvent quickly',
        'Fractional distillation of a mixture at reduced pressure',
        'Continuous extraction of a solute between immiscible phases',
        'Sublimation of a volatile solid onto a cooled surface',
      ],
      0,
      'Mundane and universal. The trap between funnel and aspirator exists because suck-back into your product is the classic first-year disaster.',
    ],
  ],
  phd: [
    [
      'What does a Hammett plot reveal?',
      [
        'How substituent electronic effects alter rate or equilibrium',
        'The order of a reaction with respect to each reactant',
        'The activation entropy from the temperature dependence of the rate',
        'The extent of conjugation from the shift in absorption maximum',
      ],
      0,
      'A negative rho means positive charge builds up. A break in the plot usually means the mechanism changes over the substituent range.',
    ],
    [
      'What is the significance of a kinetic isotope effect of 6-7 for C-H versus C-D?',
      [
        'The bond is being broken in the rate-determining step, a primary effect near the semi-classical maximum',
        'The isotope is present but not involved in the rate-determining step',
        'The reaction proceeds by a radical chain mechanism, in which the isotope affects propagation rather than initiation',
        'Tunnelling dominates the reaction at all temperatures',
      ],
      0,
      'Values well above about 7 are the usual signature of tunnelling, which is why they attract attention in enzyme mechanism work.',
    ],
    [
      'What does the 18-electron rule rationalise, and where does it fail?',
      [
        'Stability of organometallic complexes',
        'Stability of main-group hypervalent compounds, failing for period two',
        'Coordination number in lanthanide complexes, failing for the actinides',
        'Bond order in metal-metal multiple bonds, failing beyond quadruple bonds',
      ],
      0,
      'Sixteen-electron square-planar complexes are stable and common, which is why oxidative addition chemistry works at all.',
    ],
    [
      'What is a Curtin-Hammett situation?',
      [
        'Rapidly interconverting conformers whose product ratio depends on transition state energies, not conformer populations',
        'Two competing mechanisms with identical rate laws, so kinetics alone cannot distinguish them at any concentration',
        'A reaction whose selectivity is determined entirely by the more stable conformer',
        'A stepwise reaction where the first step is fully reversible and rate-limiting',
      ],
      0,
      'The most common conformer need not give the major product - which trips up product predictions made from ground-state populations.',
    ],
    [
      'In electrochemistry, what does the Butler-Volmer equation describe?',
      [
        'Current density as a function of overpotential for a charge-transfer-limited electrode reaction',
        'The equilibrium potential of a half cell from activities',
        'The diffusion-limited current at a rotating disc electrode',
        'The capacitance of the electrical double layer, as a function of electrolyte concentration and potential',
      ],
      0,
      'The diffusion-limited case is Levich; at large overpotential Butler-Volmer reduces to the Tafel relation.',
    ],
    [
      'What is the origin of the trans effect in square-planar substitution?',
      [
        'A ligand influences the lability of the position trans to it',
        'Steric crowding between cis ligands forces dissociation',
        'Crystal field stabilisation is maximised in the trans configuration',
        'Trans ligands couple through the metal\'s filled d-orbitals only',
      ],
      0,
      'It is a kinetic effect, and separating the thermodynamic trans influence from the kinetic trans effect is a routine source of confusion.',
    ],
    [
      'What does a Job plot determine?',
      [
        'The binding stoichiometry of a host-guest complex from the mole-ratio dependence of a signal',
        'The association constant from a saturation binding curve',
        'The enthalpy of binding from a temperature series, by fitting the van\'t Hoff relation to the constants',
        'The cooperativity of binding from a Hill coefficient',
      ],
      0,
      'The maximum position gives the stoichiometry. It assumes a single complex forms, and quietly misleads when several do.',
    ],
    [
      'What distinguishes chemical vapour deposition from atomic layer deposition?',
      [
        'ALD uses self-limiting sequential half-reactions',
        'ALD operates at atmospheric pressure and CVD under vacuum',
        'ALD deposits only metals and CVD only dielectrics',
        'ALD requires a plasma and CVD does not',
      ],
      0,
      'Self-limitation is what buys conformality in high-aspect-ratio features, and it is why ALD became indispensable at small nodes.',
    ],
    [
      'What is the physical basis of the chemical shift in NMR?',
      [
        'Local electron circulation shields the nucleus, altering the effective field it experiences',
        'Direct dipolar coupling between neighbouring nuclear spins through the intervening space rather than through bonds',
        'Variation in the gyromagnetic ratio between chemical environments',
        'Differences in nuclear spin relaxation rates between sites',
      ],
      0,
      'Shielding is why shift is reported in ppm - the effect scales with the applied field, so the dimensionless ratio is the transferable number.',
    ],
    [
      'What does a Pourbaix diagram show?',
      [
        'Thermodynamically stable species as a function of potential and pH',
        'Reaction rate as a function of temperature and pH',
        'Phase composition as a function of temperature and pressure',
        'Solubility as a function of ionic strength and pH',
      ],
      0,
      'Purely thermodynamic, which is why passivation regions on it can be misleading: it says nothing about how fast anything happens.',
    ],
  ],
  professor: [
    [
      'What does the Curtin-Hammett principle become in the context of dynamic kinetic resolution?',
      [
        'Rapid substrate racemisation combined with an enantioselective step can convert a racemate to one enantiomer beyond 50 per cent',
        'A racemate can be separated only up to fifty per cent yield of either enantiomer',
        'Selectivity is fixed by the relative stability of the two substrate enantiomers',
        'Catalyst turnover must exceed the racemisation rate for high selectivity, since otherwise the substrate pool depletes unevenly',
      ],
      0,
      'It breaks the yield ceiling that ordinary resolution imposes, which is why it matters industrially rather than just conceptually.',
    ],
    [
      'What is the Sabatier principle in heterogeneous catalysis?',
      [
        'Optimal activity occurs at intermediate binding strength',
        'Activity increases monotonically with the number of exposed active sites',
        'Selectivity and activity cannot be optimised simultaneously',
        'Catalytic rate is proportional to the surface free energy of the catalyst',
      ],
      0,
      'It gives the volcano plot, and computational screening of descriptors like binding energy is essentially industrialised Sabatier reasoning.',
    ],
    [
      'What does the Marcus-Hush treatment of electron transfer add to a simple activation picture?',
      [
        'Reorganisation energy of solvent and inner sphere as an explicit parameter alongside driving force',
        'A tunnelling correction for hydrogen transfer, which becomes dominant at low temperatures in enzymes',
        'Explicit treatment of the electrode double layer',
        'A statistical treatment of donor-acceptor distance distributions',
      ],
      0,
      'Lambda and delta-G together produce the inverted region, which took two decades to observe and won the Nobel when it was.',
    ],
    [
      'What is "frustrated Lewis pair" chemistry?',
      [
        'Sterically prevented acid-base adducts that retain reactivity and can heterolytically cleave dihydrogen',
        'Lewis pairs whose adduct formation is thermodynamically unfavourable at all temperatures',
        'Acid-base pairs on adjacent surface sites of a solid catalyst',
        'Pairs whose reactivity requires a transition metal mediator',
      ],
      0,
      'Stephan and Erker\'s work. Metal-free hydrogenation is the headline application, and it reframed steric frustration as a design principle.',
    ],
    [
      'What does the Bell-Evans-Polanyi relation express, and what is its modern descendant?',
      [
        'A linear correlation between activation and reaction energies',
        'A logarithmic relation between rate and equilibrium constant for acid catalysis',
        'A proportionality between activation entropy and enthalpy across a reaction series',
        'A relation between bond dissociation energy and vibrational frequency',
      ],
      0,
      'The acid-catalysis case is Bronsted. BEP relations are what make computational catalyst screening tractable, and also what limits how far it can be pushed.',
    ],
    [
      'What is the essential difficulty in single-molecule structure determination by cryo-EM at atomic resolution?',
      [
        'Dose-limited signal to noise, requiring averaging over many particles in unknown orientations',
        'Radiation damage cannot be reduced below the level that destroys secondary structure',
        'Specimens cannot be vitrified without crystalline ice formation',
        'Detector quantum efficiency remains below one per cent',
      ],
      0,
      'Direct electron detectors and better classification algorithms are what produced the resolution revolution - the dose limit itself did not move.',
    ],
    [
      'What does "orbital symmetry conservation" forbid in a concerted cycloaddition?',
      [
        'A thermal suprafacial-suprafacial 2+2 cycloaddition',
        'Any 4+2 cycloaddition under thermal conditions',
        'Photochemical 2+2 cycloadditions of alkenes',
        'Any cheletropic reaction involving a divalent species',
      ],
      0,
      'The 4+2 Diels-Alder is allowed thermally and the 2+2 is allowed photochemically - the inversion between thermal and photochemical is the whole content of the rules.',
    ],
    [
      'What is the current mechanistic consensus on the oxygen-evolving complex of photosystem II?',
      [
        'A Mn4CaO5 cluster cycling through Kok S-states',
        'A binuclear iron centre performing four sequential one-electron oxidations',
        'A copper-containing cluster analogous to that in cytochrome oxidase',
        'A manganese cluster that stores four oxidising equivalents in a single step',
      ],
      0,
      'The S-state cycle is secure and the S3-to-S0 chemistry is not, which is precisely what artificial photosynthesis needs to copy.',
    ],
    [
      'What does "machine-learned interatomic potential" change about simulation practice?',
      [
        'Near-DFT accuracy at force-field cost',
        'Exact solution of the electronic structure problem for arbitrary systems',
        'The elimination of any need for quantum-mechanical reference data',
        'Transferability guaranteed across chemistries by construction',
      ],
      0,
      'Extrapolation is the failure mode, and it fails silently - which is why active learning and uncertainty estimation are part of the method rather than extras.',
    ],
    [
      'What is the significance of "non-classical" carbocations, and why was the argument so long-running?',
      [
        'Whether the 2-norbornyl cation is a single delocalised species or rapidly equilibrating classical ions - settled crystallographically in 2013',
        'Whether carbocations exist as discrete intermediates at all, or only as transition states on a continuous coordinate with no finite lifetime',
        'Whether hyperconjugation contributes to carbocation stability',
        'Whether carbocations can be observed by NMR in superacid media',
      ],
      0,
      'Winstein against Brown for decades. Low-temperature X-ray crystallography ended it in favour of the symmetric, delocalised structure.',
    ],
  ],
};

export const MORE_BIOLOGY: CategoryContent = {
  msc: [
    [
      'What does a Southern blot detect?',
      [
        'Specific DNA sequences, by hybridisation with a labelled probe after transfer to a membrane',
        'Specific RNA transcripts, by the same hybridisation approach',
        'Specific proteins, using antibody detection after separation by polyacrylamide gel electrophoresis',
        'DNA-protein interactions in intact chromatin',
      ],
      0,
      'RNA is Northern, protein is Western. Southern is the only one named after a person, which is the standard mnemonic.',
    ],
    [
      'What is the function of a signal peptide?',
      [
        'Directing a nascent polypeptide to the endoplasmic reticulum for translocation',
        'Targeting a mature protein for proteasomal degradation',
        'Anchoring a membrane protein in the lipid bilayer',
        'Recruiting chaperones to an unfolded cytosolic protein',
      ],
      0,
      'Usually cleaved after translocation. The degradation tag is ubiquitin, and the anchor is a transmembrane domain.',
    ],
    [
      'What does the term "penetrance" mean in genetics?',
      [
        'The proportion of individuals with a genotype who show the associated phenotype',
        'The severity of the phenotype in an affected individual',
        'The proportion of offspring inheriting an allele from a carrier',
        'The frequency of an allele in a population at equilibrium',
      ],
      0,
      'Severity is expressivity, which is the term it is most often confused with.',
    ],
    [
      'What distinguishes apoptosis from necrosis?',
      [
        'Apoptosis is regulated and non-inflammatory; necrosis involves membrane rupture and inflammation',
        'Apoptosis occurs only in development and necrosis only in disease',
        'Apoptosis requires caspase activity and necrosis requires lysosomal enzymes exclusively',
        'Apoptosis affects single cells and necrosis always affects whole tissues',
      ],
      0,
      'Regulated necrotic pathways - necroptosis, pyroptosis - have since blurred the clean dichotomy considerably.',
    ],
    [
      'What is the role of telomerase?',
      [
        'Extending chromosome ends to offset the loss inherent in lagging-strand replication',
        'Repairing double-strand breaks by homologous recombination',
        'Removing supercoils ahead of the advancing replication fork, so the helix can be unwound further',
        'Proofreading newly synthesised DNA for mismatches',
      ],
      0,
      'Its reactivation in most cancers is why it is a therapeutic target, and its absence in most somatic cells is why they have a division limit.',
    ],
    [
      'What does a Punnett square fail to capture?',
      [
        'Linkage, epistasis and any departure from independent assortment',
        'The dominance relationship between two alleles',
        'The genotype frequencies expected in offspring',
        'The segregation of alleles during meiosis',
      ],
      0,
      'It assumes independent assortment, which is exactly what linked loci violate - and Bateson found that out before anyone could explain it.',
    ],
    [
      'What is the blood-brain barrier formed by?',
      [
        'Tight junctions between capillary endothelial cells, supported by astrocyte end-feet and pericytes',
        'A continuous basement membrane surrounding the cerebral vasculature',
        'The arachnoid membrane separating cerebrospinal fluid from blood',
        'Active efflux transporters in neuronal membranes, which pump xenobiotics back out into the circulation',
      ],
      0,
      'Efflux transporters are a real second line of defence but not the barrier itself, and they are why so many CNS drug candidates fail.',
    ],
    [
      'What does the term "gain of function" mutation mean?',
      [
        'A mutation conferring new or increased activity on a gene product',
        'A mutation restoring activity lost in an earlier mutation',
        'A duplication increasing the gene\'s copy number',
        'A mutation increasing transcription without altering the protein',
      ],
      0,
      'The restoring case is a reversion or suppressor. The term has a separate and much-contested usage in the virology-policy literature.',
    ],
    [
      'What is the principal function of regulatory T cells?',
      [
        'Suppressing immune responses and maintaining tolerance to self',
        'Presenting antigen on MHC class II to naive T cells',
        'Producing high-affinity antibodies after class switching',
        'Killing virus-infected cells by perforin release',
      ],
      0,
      'FOXP3 is the lineage marker, and its loss in humans produces a devastating multi-organ autoimmune syndrome.',
    ],
    [
      'What does allopatric speciation require?',
      [
        'Geographic separation preventing gene flow while divergence accumulates',
        'Divergent selection within a continuously interbreeding population',
        'Polyploidy arising within a single generation',
        'Assortative mating driven by a single major-effect locus',
      ],
      0,
      'Sympatric speciation is the second, and whether it is common remains one of the field\'s live arguments.',
    ],
  ],
  phd: [
    [
      'What does chromatin immunoprecipitation followed by sequencing measure?',
      [
        'Genome-wide occupancy of a protein',
        'Chromatin accessibility, from nuclease or transposase sensitivity',
        'Three-dimensional contact frequency between distant loci',
        'Nascent transcription rates at each locus',
      ],
      0,
      'Accessibility is ATAC-seq or DNase-seq, contacts are Hi-C, nascent transcription is one of the pro-seq family.',
    ],
    [
      'What is the molecular basis of somatic hypermutation in B cells?',
      [
        'AID-mediated cytidine deamination in the variable region',
        'Random errors of DNA polymerase during rapid clonal division',
        'RAG-mediated recombination of variable, diversity and joining segments',
        'Retroviral integration into immunoglobulin loci',
      ],
      0,
      'RAG recombination is the earlier, separate event that generates the initial repertoire; hypermutation is what affinity maturation runs on.',
    ],
    [
      'What is the Warburg effect\'s relationship to hypoxia-inducible factor?',
      [
        'HIF drives glycolytic gene expression',
        'HIF is required for the Warburg effect in all tumour types',
        'HIF suppresses glycolysis and its loss produces the effect',
        'The two are unrelated pathways with no overlapping targets',
      ],
      0,
      'Oncogenic signalling through PI3K-Akt and Myc can produce the phenotype without hypoxia, which is the observation the HIF-only account cannot accommodate.',
    ],
    [
      'What does the "neutral theory" of molecular evolution claim?',
      [
        'Most fixed substitutions are effectively neutral',
        'Natural selection plays no role in molecular evolution',
        'Substitution rates are constant across lineages by necessity',
        'Nearly all mutations are deleterious and removed by purifying selection',
      ],
      0,
      'Kimura, 1968. It is a null model, not a denial of selection - and treating it as the latter is the most common misreading.',
    ],
    [
      'What is the significance of the unfolded protein response?',
      [
        'A signalling programme that expands ER capacity, halts translation',
        'A cytosolic chaperone network refolding aggregated proteins',
        'A ubiquitin-dependent pathway degrading misfolded cytosolic proteins',
        'An autophagic route clearing damaged mitochondria',
      ],
      0,
      'PERK, ATF6 and IRE1 are the three arms. Its life-or-death switching is why it appears in secretory cancers and neurodegeneration alike.',
    ],
    [
      'In population genetics, what does the site frequency spectrum reveal?',
      [
        'Demographic history and selection',
        'The recombination rate along a chromosome',
        'The mutation rate per generation directly',
        'The physical linkage between selected and neutral loci',
      ],
      0,
      'Tajima\'s D and its relatives are summaries of it, and the difficulty is that expansion and selection distort it in similar directions.',
    ],
    [
      'What does optogenetics allow that pharmacology does not?',
      [
        'Millisecond, cell-type-specific control of activity in defined populations in a behaving animal',
        'Permanent ablation of a neuronal population without surgery',
        'Simultaneous recording of every neuron in a circuit, at single-spike resolution in a behaving animal',
        'Manipulation of gene expression without genetic modification',
      ],
      0,
      'Temporal precision plus genetic targeting is the combination. It reopened causal circuit questions that correlational recording could not settle.',
    ],
    [
      'What is the current understanding of the microbiome\'s causal role in host phenotype?',
      [
        'Associations are abundant; causal demonstrations mostly rest on gnotobiotic transfer experiments with limited generalisability',
        'Most reported associations have been shown causal by faecal transplant in humans',
        'The microbiome is now known to have no causal role in metabolic phenotype',
        'Causality has been established for immune development but nowhere else, and only in germ-free rodent models rather than in humans',
      ],
      0,
      'The reproducibility problems here are severe and mostly methodological - batch effects, compositional data and small n.',
    ],
    [
      'What does the term "haploinsufficiency" describe?',
      [
        'A single functional copy of a gene being inadequate for a normal phenotype',
        'A dominant-negative product interfering with the wild-type protein',
        'The loss of both copies of a tumour suppressor',
        'Imprinting silencing one parental allele',
      ],
      0,
      'It is why some loss-of-function mutations are dominant, and it is a distinct mechanism from a dominant-negative interaction.',
    ],
    [
      'What is the mechanism of CRISPR base editing, as distinct from nuclease editing?',
      [
        'A catalytically impaired Cas fused to a deaminase, chemically converting a base without a double-strand break',
        'A Cas nuclease creating a break repaired by a supplied single-strand template',
        'A reverse transcriptase writing new sequence from an extended guide RNA',
        'A zinc-finger nuclease pair introducing a targeted deletion, repaired by non-homologous end joining afterwards',
      ],
      0,
      'The reverse-transcriptase version is prime editing, and avoiding the double-strand break is what reduces indel and translocation risk.',
    ],
  ],
  professor: [
    [
      'What is the "missing heritability" problem, and what largely resolved it?',
      [
        'GWAS hits explained little of twin-study heritability; whole-genome approaches showed much of it lies in many small common effects',
        'Twin studies were shown to be systematically confounded and their estimates withdrawn',
        'The heritability was found to reside almost entirely in rare variants of large effect',
        'It was resolved by discovering that most complex traits are not heritable once shared environment and assortative mating are controlled',
      ],
      0,
      'Yang et al.\'s GREML work moved the accounting substantially. Rare variants and gene-environment interaction contribute but did not turn out to be the answer.',
    ],
    [
      'What does the "omnigenic" model propose?',
      [
        'Nearly all expressed genes influence complex traits through interconnected regulatory networks, via peripheral rather than core genes',
        'Every gene has a direct causal effect on every trait, so association studies necessarily recover the entire genome given a large enough sample',
        'Complex traits are driven by a small number of core genes with large effects',
        'Regulatory variants matter and coding variants do not',
      ],
      0,
      'Boyle, Li and Pritchard, 2017. The core-versus-peripheral distinction is the part critics say is not operationalised.',
    ],
    [
      'What is the current status of the "endosymbiotic" account of mitochondrial origin?',
      [
        'Firmly established, with the identity of the host lineage and the order of events still under active revision',
        'Established including the identity of the archaeal host and the timing',
        'Disputed, with autogenous origin still a mainstream alternative',
        'Established for chloroplasts but not for mitochondria, whose bacterial affinities remain entirely a matter of dispute',
      ],
      0,
      'Asgard archaea reshaped the host question substantially, and whether mitochondria came before or after eukaryogenesis is genuinely open.',
    ],
    [
      'What problem does "phylogenetic signal saturation" cause in deep-time inference?',
      [
        'Multiple substitutions at the same site erase historical information, producing artefacts like long-branch attraction',
        'Insufficient taxon sampling leaves branches unresolvable in principle',
        'Rate variation between lineages cannot be modelled at all, so every reconstruction assumes a strict molecular clock throughout',
        'Horizontal transfer makes any tree representation invalid',
      ],
      0,
      'It is why model choice matters more the deeper you go, and why amino-acid recoding and site-heterogeneous models exist.',
    ],
    [
      'What does the "hologenome" concept claim, and why is it contested?',
      [
        'That host and microbiota constitute a unit of selection',
        'That microbial genes are physically integrated into the host genome',
        'That the microbiome evolves independently of host phenotype',
        'That host genotype fully determines microbiome composition',
      ],
      0,
      'Heritability of the partner is the crux: without reliable vertical transmission, selection on the collective is hard to sustain theoretically.',
    ],
    [
      'What is the significance of AlphaFold-scale structure prediction for molecular biology?',
      [
        'Accurate models for most single domains, while complexes, disorder and conformational change remain much harder',
        'The protein folding problem is solved in all respects including dynamics',
        'Experimental structure determination is now redundant, since predicted models are accurate enough for every downstream purpose',
        'It predicts function directly from sequence without structural inference',
      ],
      0,
      'A predicted static structure is not a mechanism, and the fields where it changed practice fastest are the ones where a fold was the bottleneck.',
    ],
    [
      'What does "developmental system drift" describe?',
      [
        'Conserved phenotypes produced by divergent underlying developmental mechanisms across lineages',
        'The gradual loss of developmental plasticity in domesticated lineages',
        'Random drift in the timing of developmental events between individuals',
        'The accumulation of neutral variation in developmental genes',
      ],
      0,
      'It is why a conserved gene network is not evidence of a conserved mechanism, and it complicates every model-organism extrapolation.',
    ],
    [
      'What is the "aging as programmed versus damage" debate\'s current state?',
      [
        'Neither pole survives intact: conserved nutrient-sensing pathways modulate rates of largely stochastic damage',
        'Aging is now established as a genetically programmed developmental process, running to a schedule set at conception',
        'Aging is now established as purely stochastic damage with no genetic modulation',
        'The debate was resolved by the discovery of a single master longevity gene',
      ],
      0,
      'Insulin/IGF-1 and mTOR are the modulators; the hallmarks framework is an attempt to organise the interaction rather than to settle the dichotomy.',
    ],
    [
      'What does "clonal haematopoiesis of indeterminate potential" refer to?',
      [
        'Expanded blood-cell clones bearing leukaemia-associated mutations without overt disease, and with cardiovascular as well as cancer risk',
        'The normal oligoclonal structure of the memory T cell repertoire',
        'Donor-derived clones expanding after bone marrow transplant',
        'Somatic mosaicism confined to the germline lineage, detectable in offspring but with no consequence for the individual in whom it arose',
      ],
      0,
      'The cardiovascular association through inflammatory signalling was the genuinely surprising finding, and it connected two literatures that had not spoken.',
    ],
    [
      'What is the principal obstacle to translating senolytic therapies?',
      [
        'Senescence is heterogeneous and partly beneficial',
        'Senescent cells cannot be identified by any available marker',
        'No compound has been shown to clear senescent cells in any model',
        'Senescent cells do not accumulate with age in mammalian tissue',
      ],
      0,
      'p16 and SA-beta-gal are imperfect markers, and the fact that senescence is a tumour-suppressive programme is exactly what makes ablating it delicate.',
    ],
  ],
};
